// @ts-strict-ignore
import { BaseArticleLocationProcessor } from './base-article-location.processor';
import { OnRailReferenceArticleLocationVO } from '../model/vo/on-rail-reference-article-location.vo';
import { IArticleLocationProcessor, InsertedArticleData } from './i-article-location.processor';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Line } from '@3d-scene/model/line';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { IOperation } from '@shared/undo/operation/i-operation';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ReplaceLabelingArticleHelper } from '../../label/replace-labeling-helper/replace-labeling-article.helper';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ProjectHelper } from '@project/helper/project.helper';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleInsertProcessorRegistry } from './prepare/insert/article-insert-processor-registry';
import { ArticleManager } from '@article/manager/article.manager';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ProjectModel } from '@project/model/project.model';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { AddArticleError } from './add-article-error';

export class AddArticleOnRailLocationProcessor extends BaseArticleLocationProcessor<OnRailReferenceArticleLocationVO> implements IArticleLocationProcessor {

  constructor(
    _articleLocation: OnRailReferenceArticleLocationVO,
    _article: ArticleVO,
    projectModel: ProjectModel,
    articleManager: ArticleManager,
    insertProcessorRegistry: ArticleInsertProcessorRegistry,
    projectHelper: ProjectHelper,
    localeResourceManager: LocaleResourceManager,
    connectionInfoService: ConnectionInfoService,
    replaceBusbarHelper: ReplaceBusbarArticleHelper,
    _insertArticleHelperFactory: InsertArticleHelperFactory,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private containerService: ContainerService,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    private insertArticleUtils: InsertArticleUtils,
    private replaceBusbarService: ReplaceBusbarService
  ) {
    super(
      projectModel,
      articleManager,
      insertProcessorRegistry,
      projectHelper,
      localeResourceManager,
      _articleLocation,
      _article,
      connectionInfoService,
      replaceBusbarHelper,
      _insertArticleHelperFactory
    );

    this._articleLocation = _articleLocation;
  }

  private get articleLocation(): OnRailReferenceArticleLocationVO {
    return this._articleLocation;
  }

  public async process(): Promise<InsertedArticleData> {
    await this.loadArea();
    super.initInsertArticleHelper();
    await super.loadAndProcessFullArticle();
    await super.createAndRefreshWrapper();
    return this.insertArticle();
  }

  private async loadArea(): Promise<void> {
    this._articleContainer = this.containerService.findArticleContainerInGlobal(this.articleLocation.rangeOnLineArticleUsage.id);
    const acceptingAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(this._articleContainer.article, ConnectionAreaVO.SIDE_ACCEPTING);
    this._acceptingConnectionArea = ConnectionInfoUtils.findRangeOnLineConnectionByType(acceptingAreas, this.articleLocation.rangeOnLineType);
    if (!this._acceptingConnectionArea) {
      await super.processFailure(`Could not load accepting area`);
      const errorMessage = this.localeResourceManager.getValueByKey('addArticleOnRailLocationProcessor.message.range_on_line_connection_not_found');
      throw new AddArticleError(errorMessage)
    }
  }

  private async insertArticle(): Promise<InsertedArticleData> {
    const givingCubes: Connection[] = super.findAllGivingCubesForConnect();

    if (givingCubes && givingCubes.length > 0 && givingCubes[0].compositionRectangle) {
      const baseLine: AdvancedLine = this._insertArticleHelper.baseLine;
      const articleWidth: number = super.calculateArticleWidth(givingCubes);

      const rotationAngle: number = super.getRotateAngleOfBaseContainer(this._articlesWrapper.baseArticleContainer,
        this.articleLocation.rotationAngle);
      let articleOffset: number = this.articleLocation.articleOffset;

      const neighborArticle: ArticleContainer = this.getNeighborArticle();
      if (neighborArticle) {
        const neighborLine: AdvancedLine = this.calculateNeighborArticleLine(articleOffset, neighborArticle);
        if (neighborLine) {
          const dummyLine: AdvancedLine = AdvancedLine.createInstanceByArticle(this._article);
          if (rotationAngle === 180) {
            dummyLine.reverseOverlapProperties();
          }
          articleOffset -= AdvancedLine.calculateMaxOverlapLength(neighborLine, dummyLine);
        }
      }

      const articleLine: AdvancedLine = this.calculateArticleLine(
        articleOffset,
        articleWidth,
        baseLine,
        this.articleLocation.offsetReferencesMiddlePoint
      );
      if (rotationAngle === 180) {
        articleLine.reverseOverlapProperties();
      }
      // the part of article which is placed on the mounting rail
      const articleLineOnRail: Line = new Line(
        baseLine.hasPoint(articleLine.start) ? articleLine.start : baseLine.start,
        baseLine.hasPoint(articleLine.end) ? articleLine.end : baseLine.end
      );
      const checkBounds: boolean = ConnectionInfoUtils.isDefaultRangeOnLineConnection(this._acceptingConnectionArea);

      const busbarContainers: ArticleContainer[] = this.replaceBusbarService.getBusbarsToRecreateByInsertionPoint(
        articleLineOnRail.start,
        this._insertArticleHelper);
      // move articles
      const moveMap: Dictionary<string, Vector3> = this._insertArticleHelper.calculateMoveOffsetByArticleLine(articleLine,
        busbarContainers.map((busbar) => busbar.articleGUID), null, checkBounds);
      const labelingContainers: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByMoveMap(
        articleLineOnRail.start,
        moveMap,
        this._insertArticleHelper);

      if (moveMap && !CompositeArticleUtils.isCompositePartArticleExists(labelingContainers) && !CompositeArticleUtils.isCompositePartArticleExists(busbarContainers)) {
        const detachOperation: IOperation = await this.replaceLabelingHelper.detachLabelStrips(labelingContainers);
        if (detachOperation) {
          this._additionalUndoOperations.push(detachOperation);
        }

        await this.replaceBusbarHelper.detachBusbarContainers(busbarContainers, moveMap, this._insertArticleHelper);
        const busbarsEndOverhangChildrenMoveMap: Dictionary<string, Vector3> = this.replaceBusbarService.getEndOverhangChildrenMoveMap(this.replaceBusbarHelper.pendingBusbars, moveMap, this._insertArticleHelper);

        InsertArticleUtils.mergeMoveMap(moveMap, busbarsEndOverhangChildrenMoveMap);

        this.insertArticleUtils.moveArticlesByMap(moveMap);

        const acceptingCube: Connection = this._insertArticleHelper.createConnectionOnLine(articleLineOnRail, this._acceptingConnectionArea);

        this._insertProcessor.process(
          this._articlesWrapper.articleContainers,
          this._articlesWrapper.baseArticleContainers,
          articleLine,
          acceptingCube,
          givingCubes,
          rotationAngle,
          undefined,
          true
        );

        acceptingCube.articleContainer.disposeConnectionByName(acceptingCube.id);
        const data: InsertedArticleData = {
          baseArticleGUID: this._articlesWrapper.baseArticleContainer.articleGUID,
          insertedArticleGUIDs: ArticleContainerUtils.extractArticleGUIDs(this._articlesWrapper.articleContainers),
          moveArticlesMap: moveMap,
          articleLocation: this.articleLocation,
          createUndoOperation: true,
          undoOperations: this._additionalUndoOperations,
          isAccessoriesAdded: false,
        };
        return data;
      } else {
        const message: string = this.localeResourceManager.getValueByKey(
          'articleLocationProcessor.connect.message.error_not_enough_free_space');
        await super.processFailure(message);
        throw new AddArticleError(message);
      }
    } else {
      const connectionName: string = ArrayUtils.isEmpty(this._givingConnectionAreas) ? '' : this._givingConnectionAreas[0].name;
      const errorMessage: string = this.localeResourceManager.getValueByKey('articleLocationProcessor.message.error_unknown_connection',
        {'0': this._articlesWrapper.article.articleNo, '1': connectionName});

      await super.processFailure(`Unknown connection for existing cubes.`);
      throw new AddArticleError(errorMessage);
    }
  }

  private calculateArticleLine(moveArticleOffset: number, width: number, baseLine: Line, offsetReferencesMiddlePoint: boolean = false): AdvancedLine {
    let startPoint: Vector3;
    let endPoint: Vector3;
    if (offsetReferencesMiddlePoint) {
      startPoint = MathUtils.getInterpolated(baseLine.end, baseLine.start, (moveArticleOffset - width / 2) / baseLine.length);
      endPoint = MathUtils.getInterpolated(baseLine.end, baseLine.start, (moveArticleOffset + width / 2) / baseLine.length);
    } else {
      startPoint = MathUtils.getInterpolated(baseLine.end, baseLine.start, moveArticleOffset / baseLine.length);
      endPoint = MathUtils.getInterpolated(baseLine.end, baseLine.start, (moveArticleOffset + width) / baseLine.length);
    }
    return AdvancedLine.createInstanceByArticle(this._article, startPoint, endPoint);
  }

  private calculateNeighborArticleLine(moveArticleOffset: number, neighborArticle: ArticleContainer): AdvancedLine {
    const offset: number = NumberUtils.isNotNumber(moveArticleOffset) ? 0 : moveArticleOffset;
    const placementPoint: Vector3 = this._insertArticleHelper.calculatePointByOffset(offset);
    const neighborArticleLine: AdvancedLine = this._insertArticleHelper.getArticleLine(neighborArticle);
    if (neighborArticleLine && neighborArticleLine.hasPoint(placementPoint)) {
      return neighborArticleLine;
    }
    return null;
  }

  private getNeighborArticle(): ArticleContainer {
    const placementPoint: Vector3 = this._insertArticleHelper.calculatePointByOffset(this.articleLocation.articleOffset);
    return this._insertArticleHelper.getLeftArticleContainerByPoint(placementPoint);
  }
}
